import { useState, useRef, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';

/**
 * @param {object} props
 * @param {string} props.body - HTML content. Applied once after the editor is ready, so a body that arrives late (e.g. a transcript loaded from Firestore after mount) still shows up.
 * @param {(html: string) => void} props.setBody - Called with the new HTML on every edit.
 */
const TinyMCE = ({ setBody, body }) => {
  /** @type {React.MutableRefObject<any>} */
  const editorRef = useRef(null);
  const isEditorInitializedRef = useRef(false);
  const [editorReady, setEditorReady] = useState(false);

  /* Apply the incoming `body` exactly once, after the editor is ready.
     @tinymce/tinymce-react v3 re-runs editor.setContent() whenever a content
     prop (e.g. `initialValue`) changes between renders, which jumps the cursor
     to the start of the text. Setting content a single time via the ref —
     guarded by isEditorInitializedRef — keeps the cursor stable while still
     applying a body that arrives after mount (e.g. a transcript loaded from
     Firestore once the editor has finished initializing). */
  useEffect(() => {
    if (editorRef.current && editorReady && !isEditorInitializedRef.current && body) {
      editorRef.current.setContent(body);
      isEditorInitializedRef.current = true;
    }
  }, [body, editorReady]);

  return (
    <Editor
      onEditorChange={setBody}
      apiKey="dz1gl9k5tz59z7k2rlwj9603jg6xi0bdbce371hyw3k0auqm"
      onInit={(evt, editor) => {
        editorRef.current = editor;
        setEditorReady(true);
      }}
      init={{
        height: 500,
        menubar: false,
        statusbar: false,
        plugins: [
          'advlist autolink lists link charmap print preview anchor',
          'searchreplace visualblocks code fullscreen',
          'insertdatetime table paste code help'
        ],
        toolbar: 'undo redo | formatselect | bold italic backcolor | ' +
          'alignleft aligncenter alignright alignjustify | ' +
          'bullist numlist outdent indent | removeformat | help',
        content_style: 'body { font-family: Raleway, sans-serif; font-size:14px; color: gray }'
      }}
    />
  );
}

export default TinyMCE;
